// src/modules/reclamos/components/ReclamoModal.jsx
import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, FormControl, InputLabel, Select,
  Snackbar, Alert
} from "@mui/material";
import { reclamosApi } from "../services/reclamosApi";

const DEP_TECNICO_SENTINEL = "__DEP_TECNICO__";

export default function ReclamoModal({
  open,
  onClose,
  onSuccessNavigate,
  initialTitle,   // <-- nuevo: título sugerido (placa o modem)
  initialData,    // <-- nuevo: datos extra opcionales (placa, modem)
}) {
  const [form, setForm] = useState({
    titulo: "",
    descripcion: "",
    prioridad: "BAJA",
    id_responsable: DEP_TECNICO_SENTINEL,
    id_categoria: "",
  });
  const [categorias, setCategorias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);

  // Snackbar local
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });
  const openSnack = (msg, severity = "info") => setSnack({ open: true, msg, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // cuando se abre, cargamos combos y también seteamos el título inicial
  useEffect(() => {
    if (!open) return;
    setSnack({ open: false, msg: "", severity: "info" });

    // prellenar el título si viene de afuera
    setForm((prev) => ({
      ...prev,
      titulo: initialTitle || "",   // <-- aquí entra "Reclamo unidad: XXX" o "Reclamo módem: YYY"
    }));

    (async () => {
      try {
        const [cats, usrs] = await Promise.all([
          reclamosApi.categorias({ activo: 1 }),
          reclamosApi.usuariosTRS({ role: "sistemas,tecnico,admin" }),
        ]);
        setCategorias(cats || []);
        setUsuarios(usrs || []);
      } catch (e) {
        setCategorias([]);
        setUsuarios([]);
        openSnack("No se pudieron cargar categorías/usuarios", "error");
      }
    })();
  }, [open, initialTitle]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const isDeptTecnico = form.id_responsable === DEP_TECNICO_SENTINEL;

      const payloadBase = {
        ...form,
        id_responsable: isDeptTecnico
          ? null
          : form.id_responsable
          ? Number(form.id_responsable)
          : null,
        id_categoria: form.id_categoria ? Number(form.id_categoria) : null,
      };

      // si te interesa guardar placa/modem dentro del reclamo:
      const payload = {
        ...payloadBase,
        ...initialData, // { placa: "...", modem: "..." }
      };

      const res = await reclamosApi.crear(payload);
      openSnack(`Reclamo ${res.codigo ?? ""} creado`, "success");
      onSuccessNavigate?.(res.id);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err.message;
      openSnack(
        `Error${status ? ` (${status})` : ""}: ${msg}`,
        "error"
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const disabledSubmit =
    loading || !form.titulo.trim() || !form.descripcion.trim();

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {initialTitle ? initialTitle : "Nuevo Reclamo"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            name="titulo"
            label="Placa"
            fullWidth
            sx={{ mt: 2 }}
            value={form.titulo}
            onChange={handleChange}
          />
          <TextField
            name="descripcion"
            label="Descripción"
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 2 }}
            value={form.descripcion}
            onChange={handleChange}
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="prio">Prioridad</InputLabel>
            <Select
              labelId="prio"
              name="prioridad"
              label="Prioridad"
              value={form.prioridad}
              onChange={handleChange}
            >
              {["BAJA", "MEDIA", "ALTA", "URGENTE"].map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="cat">Categoría</InputLabel>
            <Select
              labelId="cat"
              name="id_categoria"
              label="Categoría"
              value={form.id_categoria}
              onChange={handleChange}
              displayEmpty
            >
              <MenuItem value="">
                <em>Sin categoría</em>
              </MenuItem>
              {categorias.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="resp">Responsable</InputLabel>
            <Select
              labelId="resp"
              name="id_responsable"
              label="Responsable"
              value={form.id_responsable}
              onChange={handleChange}
              displayEmpty
            >
              <MenuItem value={DEP_TECNICO_SENTINEL}>
                <em>Departamento Técnico (bandeja)</em>
              </MenuItem>
              {usuarios.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.nombre ?? u.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={disabledSubmit}
          >
            {loading ? "Creando..." : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnack}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
